import { Play } from '@phosphor-icons/react/ssr';
import type { ComponentProps, ReactNode } from 'react';

import { tv } from '../../lib/tv';

/**
 * The Figma "Thumbnail 9:16" frame used by Hover Card (Reel) and the Drawer
 * example content: a 9:16 placeholder gradient (olive/950 → olive/900; Figma's
 * gradient transform is ≈127° on both frames), a brand/primary Play badge
 * with an icon/primary glyph, and a Duration chip (bg/inverse, Caption
 * text/inverse, radius 4) in the bottom-right corner. An `img` / `video` child
 * fills the frame.
 */
const reelThumbnail = tv({
  slots: {
    root: [
      'relative flex shrink-0 items-center justify-center overflow-hidden',
      'bg-[linear-gradient(127deg,var(--color-olive-950)_7%,var(--color-olive-900)_79%)]',
      '[&>img,&>video]:absolute [&>img,&>video]:inset-0 [&>img,&>video]:size-full [&>img,&>video]:object-cover',
    ],
    play: 'bg-brand-primary text-icon-primary relative flex items-center justify-center rounded-full',
    duration: 'bg-bg-inverse text-caption text-text-inverse absolute rounded-xs py-px',
  },
  variants: {
    size: {
      // Hover Card Reel: 72 × 128, radius 8, Play 28 (14 glyph), chip 6 from the corner.
      sm: {
        root: 'h-32 w-18 rounded-md',
        play: 'size-7 [&>svg]:size-3.5',
        duration: 'right-1.5 bottom-1.5 px-1',
      },
      // Drawer: 112 × 200, radius 10, Play 36 (16 glyph), chip 8 from the corner.
      lg: {
        root: 'h-50 w-28 rounded-[10px]',
        play: 'size-9 [&>svg]:size-4',
        duration: 'right-2 bottom-2 px-[5px]',
      },
    },
  },
  defaultVariants: { size: 'sm' },
});

export type ReelThumbnailProps = ComponentProps<'div'> & {
  size?: 'sm' | 'lg';
  /** Duration chip text, e.g. "0:30". */
  duration?: ReactNode;
  /** Show the Play badge (decorative). */
  playable?: boolean;
};

export function ReelThumbnail({
  size,
  duration,
  playable = true,
  className,
  children,
  ...props
}: ReelThumbnailProps) {
  const styles = reelThumbnail({ size });
  return (
    <div data-slot="reel-thumbnail" className={styles.root({ className })} {...props}>
      {children}
      {playable && (
        <span data-slot="reel-thumbnail-play" aria-hidden className={styles.play()}>
          <Play />
        </span>
      )}
      {duration !== undefined && (
        <span data-slot="reel-thumbnail-duration" className={styles.duration()}>
          {duration}
        </span>
      )}
    </div>
  );
}
